import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import cn from '@/lib/clsxm';
import { APIErrorResponseGenerator } from '@/lib/helper';
import handleGA4Event, { eventTracker } from '@/lib/service-workers';

import Button from '@/components/@base/button';
import Container from '@/components/@base/container';
import { LOADING_COLORS } from '@/components/@base/loading/type';
import { showToast } from '@/components/@base/toast/show-toast';
import { Text } from '@/components/@base/typography';
import { usePostAddToCart } from '@/components/app/cart/services/hooks';
import Props from '@/components/app/product/components/side-bar/purchase-history/history-item/type';
import { useGetProfile } from '@/components/layout/services/hooks';

import { pageLevelLocalization } from '@/constant/localization';
import { GET_CART } from '@/constant/query-key';

const { product: productLocalization, cart: cartLocalization } = pageLevelLocalization;

export default function HistoryItem({
  domain,
  supportLeftDay,
  itemId,
  productId,
  className,
}: Props) {
  const { mutate: addToCartMutate, isPending } = usePostAddToCart();
  const queryClient = useQueryClient();
  const { profile } = useGetProfile();

  const handleSupportExtensionClick = (relatedItemId: string) => {
    if (relatedItemId) {
      addToCartMutate(
        {
          id: productId,
          related_item: relatedItemId,
          slug: 'renew-support',
        },
        {
          onError: (e) => {
            if (e instanceof AxiosError) showToast(APIErrorResponseGenerator(e));
          },
          onSuccess: (res) => {
            queryClient.setQueryData([GET_CART], res);
            eventTracker(profile?.mobile, 'add_to_basket', new Date().toISOString(), {
              productId,
            });
            const product = res?.products?.find(
              (item) => item?.params?.product_id === productId,
            );
            handleGA4Event(
              'add_to_cart',
              [
                {
                  item_id: productId,
                  item_name: product?.title,
                  price: product?.price,
                  index: 0,
                  quantity: 1,
                  affiliation: product?.store?.slug,
                  discount: product?.discount,
                },
              ],
              {
                currency: 'USD',
                value: res?.total,
              },
            );
            showToast({ message: cartLocalization.productSuccessfullyAdded, type: 'success' });
          },
        },
      );
    }
  };
  return (
    <Container center className={cn('w-full justify-between', className)}>
      <Container>
        <Text className='text-[15px] font-bold text-[#424244]'>
          {domain || productLocalization.noDomain}
        </Text>
        <Text className='text-sm font-medium text-[#76767C]'>
          {supportLeftDay > 0
            ? productLocalization.supportRemainingDays(supportLeftDay)
            : productLocalization.supportExpired}
        </Text>
      </Container>
      <Button
        onClick={() => handleSupportExtensionClick(itemId)}
        isLoading={isPending}
        loadingColor={LOADING_COLORS.ORANGE}
        className='h-[46px] w-[110px] rounded-md bg-[#FFAB2214] text-sm font-bold text-[#FF9606] hover:bg-[#FFB616] hover:text-[#FFFFFF]'
      >
        {productLocalization.supportExtension}
      </Button>
    </Container>
  );
}
